import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export type UserRole = 'admin' | 'doctor' | null;

interface DoctorAdmin {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: UserRole;
  doctorProfile: DoctorAdmin | null;
  signIn: (email: string, password: string) => Promise<UserRole>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [doctorProfile, setDoctorProfile] = useState<DoctorAdmin | null>(null);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data: doctorData, error: doctorError } = await supabase
        .from('doctor_admins')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (doctorError) throw doctorError;

      if (doctorData) {
        setUserRole('doctor');
        setDoctorProfile(doctorData);
      } else {
        setUserRole('admin');
        setDoctorProfile(null);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole('admin');
      setDoctorProfile(null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchUserRole(session.user.id);
        } else {
          setUserRole(null);
          setDoctorProfile(null);
        }
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string): Promise<UserRole> => {
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) {
      const { data: doctorData } = await supabase
        .from('doctor_admins')
        .select('*')
        .eq('user_id', data.user.id)
        .maybeSingle();

      const role = doctorData ? 'doctor' : 'admin';
      setUserRole(role);
      setDoctorProfile(doctorData);
      return role;
    }
    return null;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserRole(null);
    setDoctorProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, userRole, doctorProfile, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
